import { IAugmentedJQuery, IController, IIntervalService, IPromise, IQService, IScope, ITimeoutService, translate } from 'angular';
import { ICodeInputScope } from './codeInput.directive';
import { ICodeInputConfig } from './codeInput.config';
import { ICodeInputOnCompleteFillingProps, IOnSendCodeResponse } from './codeInput.types';
const actionKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'] as const;
type ActionKey = typeof actionKeys[number];
const keys = new Set<string>(actionKeys);
export const iosDelay = 50;
export const focusDelay = 25;
export const completeFillingDelay = 400;
export interface ICodeInputController extends IController, ICodeInputScope {
  values: string[];
  minIndex: number;
  maxIndex: number;
  showButton: boolean;
  timer: number;
  timerHtml: string;
  focusOn: number;
  pending: boolean;
  intervalPromise?: IPromise<number>;
  completeFillingPromise?: IPromise<void>;
  onKeydown(event: KeyboardEvent, index: number): void;
  onPaste(event: ClipboardEvent): void;
  onChange(index: number): void;
  onButtonClick(): void;
  onRetry(): void;
}
export default class CodeInputController implements ICodeInputController {
  length?: number;
  focusOnStart?: boolean;
  disabled?: boolean;
  requireSendCode?: boolean;
  description?: string;
  sendCodeDelay?: number;
  sendText?: string;
  resendText?: string;
  onCompleteFilling?: (props: ICodeInputOnCompleteFillingProps) => IPromise<unknown> | void;
  onSendCode?: () => IPromise<IOnSendCodeResponse>;
  intervalPromise?: IPromise<number>;
  completeFillingPromise?: IPromise<void>;
  values: string[] = [];
  minIndex = 0;
  maxIndex = 0;
  showButton = false;
  timer = 0;
  timerHtml = '';
  focusOn = -1;
  pending = false;
  private completed = false;

  /* @ngInject */
  constructor(readonly codeInputConfig: ICodeInputConfig, readonly $element: IAugmentedJQuery, readonly $timeout: ITimeoutService, readonly $scope: IScope, readonly $interval: IIntervalService, readonly $translate: translate.ITranslateService, readonly $q: IQService) {}
  $onInit() {
    this.length ??= this.codeInputConfig.defaultCodeLength;
    this.focusOnStart ??= this.codeInputConfig.defaultFocusOnStart;
    this.disabled ??= this.codeInputConfig.defaultDisabled;
    this.sendText ??= this.codeInputConfig.defaultSendText;
    this.resendText ??= this.codeInputConfig.defaultResendText;
    this.showButton = (this.requireSendCode ??= this.codeInputConfig.defaultRequireSendCode) && this.onSendCode != null;
    this.values = new Array(this.length);
    this.maxIndex = this.length - 1;
    if (this.focusOnStart) {
      this.$timeout(() => {
        this.moveFocusOn(this.minIndex);
      });
    }
    if (!this.showButton) {
      this.setTimer();
    }
  }
  $onDestroy() {
    if (this.intervalPromise != null) {
      this.$interval.cancel(this.intervalPromise);
    }
    this.cancelCompleteFilling();
  }
  $postLink() {
    const element = this.$element[0];
    const onPaste = (event: ClipboardEvent) => {
      this.onPaste(event);
    };
    let pointerType = 'mouse';
    const onPointerDown = (event: PointerEvent) => {
      pointerType = event.pointerType;
    };
    const onMouseDown = (event: Event) => {
      const input = this.getEventInput(event);
      if (input != null && pointerType === 'mouse' && document.activeElement === input) {
        event.preventDefault();
        input.select();
      }
    };
    const onClick = (event: Event) => {
      const input = this.getEventInput(event);
      if (input != null && pointerType !== 'mouse' && document.activeElement === input) {
        input.select();
      }
    };
    const onFocusIn = (event: Event) => {
      const input = this.getEventInput(event);
      if (input == null) {
        return;
      }
      const index = this.getInputs().indexOf(input);
      if (!this.isNumber(this.values[index])) {
        const fillIndex = this.getFillIndex(index);
        if (fillIndex !== index) {
          this.moveFocusOn(fillIndex);
          return;
        }
      }
      input.select();
    };
    const onInput = (event: Event) => {
      this.getEventInput(event)?.select();
    };
    element.addEventListener('paste', onPaste);
    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('mousedown', onMouseDown);
    element.addEventListener('click', onClick);
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('input', onInput);
    this.$scope.$on('$destroy', () => {
      element.removeEventListener('paste', onPaste);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('mousedown', onMouseDown);
      element.removeEventListener('click', onClick);
      element.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('input', onInput);
    });
  }
  onKeydown(event: KeyboardEvent, index: number) {
    const key = this.getEventKey(event);
    if (!this.isActionKey(key)) {
      return;
    }
    const actions: Record<ActionKey, () => void> = {
      ArrowLeft: () => {
        this.moveFocusOn(index - 1);
      },
      ArrowRight: () => {
        this.moveFocusOn(index + 1);
      },
      Backspace: () => {
        this.delete(event, index);
      },
      Delete: () => {
        this.delete(event, index);
      }
    };
    actions[key]();
  }
  onPaste(event: ClipboardEvent) {
    this.validateLength();
    if (event.clipboardData == null) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const clipboardValue = this.normalizedText(event.clipboardData.getData('text/plain'));
    if (clipboardValue == null || clipboardValue === '') {
      return;
    }
    this.onPasteProcess(clipboardValue, 0);
  }
  onChange(index: number) {
    this.cancelCompleteFilling();
    this.values[index] = this.normalizedText(this.values[index]);
    if (this.values[index] == null || this.values[index] === '') {
      return;
    }
    const oldValue = this.values[index];
    this.$timeout(() => {
      if (oldValue !== this.values[index]) {
        return;
      }
      if (oldValue.length > 1) {
        this.onPasteProcess(oldValue, index);
      } else {
        this.moveFocusOn(index + 1);
        this.process();
      }
    }, iosDelay);
  }
  onButtonClick() {
    this.validateOnButtonClick();
    this.onSendCode().then(response => {
      this.showButton = !response.success;
      if (response.success) {
        this.setTimer();
      }
    });
  }
  onRetry() {
    if (this.onSendCode != null && this.timer === 0) {
      this.onSendCode();
      this.setTimer();
    }
  }
  private delete(event: KeyboardEvent, index: number) {
    event.preventDefault();
    this.cancelCompleteFilling();
    if (!this.isNumber(this.values[index]) && index > this.minIndex) {
      this.moveFocusOn(index - 1);
      this.values[index - 1] = '';
    } else {
      this.values[index] = '';
    }
    if (this.isEmpty()) {
      this.completed = false;
    }
  }
  private isEmpty(): boolean {
    return this.values.every(value => !this.isNumber(value));
  }

  //Unidentified - on mobile
  private getEventKey(event: KeyboardEvent): string {
    return event.key !== 'Unidentified' ? event.key : event.target instanceof HTMLInputElement ? event.target.value : '';
  }
  private getInputs(): HTMLInputElement[] {
    return Array.from(this.$element[0].querySelectorAll('input'));
  }
  private getEventInput(event: Event): HTMLInputElement | null {
    return event.target instanceof HTMLInputElement && this.$element[0].contains(event.target) ? event.target : null;
  }
  private getFillIndex(index: number): number {
    for (let i = this.minIndex; i < index; i++) {
      if (!this.isNumber(this.values[i])) {
        return i;
      }
    }
    return index;
  }
  private cancelCompleteFilling() {
    if (this.completeFillingPromise != null) {
      this.$timeout.cancel(this.completeFillingPromise);
      this.completeFillingPromise = undefined;
    }
  }
  private process() {
    let valid = true;
    for (let i = 0; i < this.values.length; i++) {
      if (this.values[i] == null || this.values[i] === '' || !this.isNumber(this.values[i])) {
        valid = false;
        this.values[i] = '';
      }
    }
    if (!valid) {
      return;
    }
    const onCompleteFilling = this.onCompleteFilling;
    if (onCompleteFilling == null || this.pending) {
      return;
    }
    this.cancelCompleteFilling();
    const delay = this.completed ? completeFillingDelay : 0;
    this.completed = true;
    this.completeFillingPromise = this.$timeout(() => {
      this.completeFillingPromise = undefined;
      this.pending = true;
      const result = onCompleteFilling({
        code: this.values.join('')
      });
      const complete = () => {
        this.pending = false;
      };
      this.$q.when(result).then(complete, complete);
    }, delay);
  }
  private isNumber(value: any): boolean {
    return /\d/u.test(value);
  }
  private clearValues() {
    for (let i = 0; i < this.values.length; i++) {
      this.values[i] = '';
    }
    this.completed = false;
    this.moveFocusOn(0);
  }
  private normalizedText(text?: string) {
    if (text == null || text === '') {
      return '';
    }
    return text.replaceAll(/\D/gu, '');
  }
  private moveFocusOn(index: number) {
    let focusOn = index;
    if (index > this.maxIndex) {
      focusOn = this.maxIndex;
    } else if (index < this.minIndex) {
      focusOn = this.minIndex;
    }
    this.focusOn = focusOn;
    const element = this.$element[0].querySelectorAll('input')[focusOn];
    this.$timeout(() => {
      element.select();
    }, focusDelay);
  }
  private setTimer() {
    if (this.intervalPromise != null) {
      this.$interval.cancel(this.intervalPromise);
    }
    if (this.sendCodeDelay == null || this.sendCodeDelay <= 0) {
      this.timer = this.codeInputConfig.defaultSendCodeDelay;
    } else {
      this.timer = this.sendCodeDelay;
    }
    this.intervalPromise = this.$interval(() => {
      if (this.timer > 0) {
        this.timerHtml = this.$translate.instant('Js.Login.Code.RetryPhoneCountdownText', {
          sec: this.timer
        });
        this.timer--;
      } else {
        this.timerHtml = '';
        if (this.intervalPromise != null) {
          this.$interval.cancel(this.intervalPromise);
        }
      }
    }, 1000, this.timer);
  }
  private onPasteProcess(code: string, index: number): void {
    this.cancelCompleteFilling();
    this.validateLength();
    if (code.length > this.length) {
      index = 0;
    }
    if (index === 0) {
      this.clearValues();
    }
    for (let i = 0; i < code.length && index < this.length; i++) {
      this.values[index] = code[i];
      index++;
    }
    this.moveFocusOn(index);
    this.process();
  }
  private validateOnButtonClick(): asserts this is {
    onSendCode: () => IPromise<IOnSendCodeResponse>;
  } {
    if (this.onSendCode == null) {
      throw new Error('Code input: OnButtonClick function onSendCode was null or undefined');
    }
  }
  private validateLength(): asserts this is {
    length: number;
  } {
    if (this.length == null) {
      throw new Error('Code input: Length is required');
    }
  }
  private isActionKey(key: string): key is ActionKey {
    return keys.has(key);
  }
}
CodeInputController.$inject = ["codeInputConfig", "$element", "$timeout", "$scope", "$interval", "$translate", "$q"];